import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2, AlertCircle, ChevronDown, Info } from "lucide-react"; // Added Info icon
import { machinesAPI } from "../api";
import { Modal } from "./ui";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

const statusOptions = {
  operational: "Operational",
  maintenance: "In Maintenance",
  error: "Error",
  offline: "Offline",
};

const MachineConfigModal = ({ isOpen, onClose, machine }) => {
  const queryClient = useQueryClient();
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const statusRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    model: "",
    manufacturer: "",
    serialNumber: "",
    locationInPlant: "",
    status: "",
  });

  useEffect(() => {
    if (machine) {
      setFormData({
        name: machine.name || "",
        model: machine.model || "",
        manufacturer: machine.manufacturer || "",
        serialNumber: machine.serial_number || "",
        locationInPlant: machine.location_in_plant || "",
        status: machine.status || "operational",
      });
    }
  }, [machine, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target))
        setIsStatusOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (data) => machinesAPI.update(machine.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machine", machine.id] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine configuration updated");
      onClose();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.error || "Failed to update configuration",
      ),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Machine"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Machine Name</label>
            <input
              type="text"
              className="input input-field"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>
          <div className="relative" ref={statusRef}>
            <label className="label">Status</label>
            <button
              type="button"
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="input input-field text-left w-full flex justify-between items-center"
            >
              <span>{statusOptions[formData.status]}</span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isStatusOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  // Dropdown remains white
                  className="absolute z-10 mt-1 w-full card bg-white p-1 shadow-lg"
                >
                  {Object.entries(statusOptions).map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => {
                        setFormData({ ...formData, status: key });
                        setIsStatusOpen(false);
                      }}
                      className="dropdown-item w-full text-left"
                    >
                      {label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        {/* ... other form fields ... */}
        {/* Changed from yellow to blue for an 'information' tone, while keeping the functionality the same */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 mt-4">
          {/* Changed icon color to blue */}
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Updating core machine metadata will affect historical logs and
            scheduled maintenance linked to this machine ID.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            // Primary button is typically blue, so we keep this class
            className="btn btn-primary min-w-[140px] shadow-md"
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default MachineConfigModal;
